import React, { useEffect, useState, useRef, useMemo } from 'react'
import {
  Form,
  Input,
  Button,
  Typography,
  Dropdown,
  Select,
  DatePicker,
  Progress,
  Tooltip,
} from 'antd'

import { QrcodeOutlined } from '@ant-design/icons'

import i18n from 'i18n'
import dayjs from 'dayjs'

import classNames from 'classnames'

import { useTranslation } from 'react-i18next'

import { store as reduxStore } from 'index'
import store from 'store'

import { ReactSortable } from 'react-sortablejs'
import {
  getInitialAvatarSrc,
  CopyPopoverComponent,
  getCopyFunc,
  confirmBase,
  passwordStrength,
  errorTranslationMessage,
} from 'utils/ui'

import { openFile, getTextFromHTML } from 'utils'
import sanitizeHtml from 'utils/sanitize'
import { EMAIL_PATTERN } from 'utils/recordValidation'
import { isGlobal } from 'conf'
import { regexLeakedPassword } from 'dUtils'

import { featureFlags, isFeatureHiddenType } from 'config/feature-flags'
import { isTotpCreateAllowed, isTotpViewAllowed } from 'utils/plan'
import TotpCountdownRing from 'pages/records/components/TotpCountdownRing'
import { getTotpCodeStyle } from 'pages/records/components/totpCodeStyle'
import TotpQrModal from 'pages/records/components/TotpQrModal'
import { isDuplicateFilename } from 'pages/records/components/Attachment/duplicateFilename'
import { computeChangedFields, getActivityFieldType } from 'pages/records/components/changedFields'
import { normalizeToOtpauthUri, createGenerator, extractSecret, replaceSecret } from 'services/totp'
import { decodeTotpQrImage } from 'services/totp/qrImage'
import { getClockDriftMs, CLOCK_DRIFT_BANNER_MS } from 'services/totp/timeSync'
import { subscribeTotpTick } from 'services/totp/ticker'

import FeatherIcon from 'components/FeatherIcon'

import attachmentHelper, {
  getAttachmentSizeLimit,
} from 'pages/records/components/Attachment/helper'
import Attachment from 'pages/records/components/Attachment'
import PasswordGenerator from 'pages/records/components/PasswordGenerator'

import ResourceTemplateService from 'api/ResourceTemplate'
import ActivityService from 'api/Activity'
import { isMemoTemplate } from 'pages/records/components/MemoEditorModal/memoRecord'
import { shouldHideAuthenticatorTemplate } from 'pages/records/components/authenticatorTemplate'
import styles from 'pages/records/components/CardView/style.module.scss'

import trashIcon from '@/assets/images/trash-24.svg'
import dragIcon from '@/assets/images/icon-drag-24.svg'
import plusIcon from '@/assets/images/icon-blue-plus-16.svg'
import lockIcon from '@/assets/images/icon-lock.svg'
import lockIconSecondary from '@/assets/images/icon-lock-secondary-16.svg'
import eyeIcon from '@/assets/images/icon-eye.svg'
import eyeOffIcon from '@/assets/images/icon-eye-off.svg'
import paperClip24SVG from '@/assets/images/paper-clip-24.svg'
import linkIcon from '@/assets/images/external-link-black-20.svg'
import keyIcon from '@/assets/images/icon-key.svg'
import calendarIcon from '@/assets/images/icon-calendar-24.svg'
import redLockIcon from '@/assets/images/icon-dark-web-rock.svg'
import autofillIcon from '@/assets/images/icon-autofill-16.svg'
import autofillIconSecondary from '@/assets/images/icon-autofill-secondary-16.svg'
import requiredBadgeIcon from '@/assets/images/icon-required-badge-16.svg'

import styles0 from './style.module.scss'

// 템플릿 캐시 - 중복 API 호출 방지
let templateCache = null
let templateCachePromise = null

const { Link } = Typography
const { TextArea } = Input

const formatSizeLimitLabel = (bytes) => `${Math.round(bytes / (1024 * 1024))}MB`

export const IMPORT_EXPORT_CONFIG = (() => {
  const isConfig = true
  // const isConfig = false

  return {
    enableImport: isConfig,
    enableExport: isConfig,
  }
})()

export const SRC_TYPE = {
  ORGCHART_USER: 0,
  GROUP_ITEM: 1,
  SHARED_USER: 2,
  SHARED_GROUP: 3,
  GROUP_OWNER: 4,
  GROUP_USER: 5,
  GROUP_ADMIN: 7,
  OWNER: 10,
  RESOURCE_OWNER: 15,
  GLOBAL_USER: 16,
}

export const getAvatarURL = (name, email, orgChartData) => {
  let url = null
  if (email != null && orgChartData != null && orgChartData.users != null) {
    const user = orgChartData.users.find((item) => item.data.account === email)
    if (user != null) {
      url = user.data.profile_photo_url
    }
  }

  let imgSrc = null
  if (url == null || url.trim().length === 0 || url.indexOf('my_default') > -1) {
    imgSrc = getInitialAvatarSrc(name, email)
  } else {
    imgSrc = url
  }

  return imgSrc
}

export const createUserData = (sourceType, originData, orgChartData) => {
  let data = null

  // sourceType: 0 - orgchart user data, 1 - shared user data, 2 - shared Group data, 10 - owner.
  if (sourceType === SRC_TYPE.ORGCHART_USER) {
    // orgchart user data - share되기 전 상태.
    const { id } = originData
    const name = isGlobal() ? originData.user_name : originData.display_name
    const email = isGlobal() ? originData.user_email : originData.account
    const expired = isGlobal() ? originData.expired : null

    data = {
      sourceType,
      id,
      type: 'user',
      name,
      email,
      avatarUrl: getAvatarURL(name, email, orgChartData),
      originData,
      permission: 1,
      expiredAt: expired,
    }
  } else if (sourceType === SRC_TYPE.GROUP_ITEM) {
    // group item - share되기 전 상태.
    const { id, name } = originData
    data = {
      sourceType,
      id,
      type: 'group',
      name,
      email: null,
      originData,
      permission: 1,
      expiredAt: null,
    }
  } else if (sourceType === SRC_TYPE.SHARED_USER) {
    // shared user data.
    const { id, type, name, email, permission, expired_at: expiredAt, expired, active } = originData
    data = {
      sourceType,
      id,
      type,
      name,
      email,
      avatarUrl: getAvatarURL(name, email, orgChartData),
      originData,
      permission,
      expiredAt,
      expired,
      active,
    }
  } else if (sourceType === SRC_TYPE.SHARED_GROUP) {
    // shared user data.
    const { id, type, name, email, permission, expired_at: expiredAt, expired } = originData
    data = {
      sourceType,
      id,
      type,
      name,
      email,
      originData,
      permission,
      expiredAt,
      expired,
    }
  } else if (sourceType === SRC_TYPE.GROUP_OWNER) {
    // shared user data.
    const { user_name: name, user_email: email, id } = originData
    data = {
      sourceType,
      id,
      type: 'user',
      name,
      email,
      avatarUrl: getAvatarURL(name, email, orgChartData),
      originData,
    }
  } else if (sourceType === SRC_TYPE.GROUP_USER) {
    // shared user data.
    const { user_name: name, user_email: email, id, avatar_url: avatarUrl } = originData
    data = {
      sourceType,
      id,
      type: 'user',
      name,
      email,
      avatarUrl: avatarUrl ?? getAvatarURL(name, email, orgChartData),
      originData,
    }
  } else if (sourceType === SRC_TYPE.OWNER) {
    // owner
    const { id, name, email } = originData // creator.
    data = {
      sourceType,
      id,
      type: 'user',
      name,
      email,
      avatarUrl: getAvatarURL(name, email, orgChartData),
      originData,
    }
  } else if (sourceType === SRC_TYPE.GLOBAL_USER) {
    // global user data.
    const { id, user_name: name, user_email: email, expired } = originData
    data = {
      sourceType,
      id,
      type: 'user',
      name,
      email,
      avatarUrl: getAvatarURL(name, email, orgChartData),
      originData,
      expiredAt: null,
      expired,
    }
  }

  return data
}

const SecureField = ({ content, styles2, type, actionRequest, messageApi }) => {
  const [visible, setVisible] = useState(true)
  const { t } = useTranslation()

  const togglePasswordType = async () => {
    setVisible((prev) => !prev)
    if (visible === true) {
      try {
        await ActivityService.post(actionRequest)
      } catch (error) {
        const { code } = error
        if (error != null) {
          errorTranslationMessage(code, t, messageApi)
        }
      }
    }
  }

  const replaceText = () => {
    const pwStr = type !== 'pincode' ? '*' : '*'
    if (!visible) {
      return content
    }

    if (type === 'secure_email') {
      return content
        .replace(/(?<=.{2}).*(?=@)/, '*'.repeat(8))
        .replace(/(?<=@.{2}).*(?=\.)/, '*'.repeat(8))
        .replace(/\.[^.]+$/, '')
    }
    return pwStr.repeat(8)
  }

  return (
    <React.Fragment>
      <span>{replaceText()}</span>
      <div className={classNames(styles2.DetailPasswordDiv)}>
        <Button
          className={classNames('disable-copy')}
          type="text"
          icon={<img src={visible ? eyeOffIcon : eyeIcon} alt="password" />}
          onClick={togglePasswordType}
          tabIndex={-1}
        />
      </div>
    </React.Fragment>
  )
}

// 자릿수에 맞춰 코드를 두 그룹으로 표시(6→3+3, 8→4+4, 그 외 절반 분할).
const formatTotpCode = (code) => {
  if (code.length === 6) return `${code.slice(0, 3)} ${code.slice(3)}`
  if (code.length === 8) return `${code.slice(0, 4)} ${code.slice(4)}`
  const mid = Math.ceil(code.length / 2)
  return `${code.slice(0, mid)} ${code.slice(mid)}`
}

// TOTP 상세 뷰: 복호된 otpauth URI(field.content)로 30초 회전 코드 + 잔여시간 표시. 코드 클릭 시 복사.
// export는 통합 테스트(TotpFieldView.test.js — 드리프트 배너 분기 검증)용.
export const TotpFieldView = ({ content, styles2, onCopyCode, onShow, onShowQr, recordTitle }) => {
  const { t } = useTranslation()
  const [state, setState] = useState(null)
  const [qrOpen, setQrOpen] = useState(false)

  // URI는 content 변경 시 1회만 파싱하고, 매 tick은 코드/잔여시간만 재계산
  const generator = useMemo(() => {
    try {
      return { fn: createGenerator(content) }
    } catch (e) {
      return { error: true }
    }
  }, [content])

  // totp는 마스킹 없이 코드를 바로 노출(design §4.2)하므로 "표시=열람"으로 보고 mount 시 감사 이벤트 1회 기록.
  // SecureField는 눈 아이콘 클릭-reveal 시 기록하지만, totp는 마스킹이 없어 트리거 시점이 다름(의도된 차이).
  useEffect(() => {
    if (onShow) onShow()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (generator.error === true) {
      setState({ error: true })
      return undefined
    }
    const tick = () => setState({ ...generator.fn(), driftMs: getClockDriftMs() })
    tick()
    // 개별 setInterval 대신 공용 티커 구독 — 칩/상세가 같은 순간 재계산돼 잔여초 표시가
    // ±1초 어긋나 보이는 문제를 제거(services/totp/ticker 참조).
    return subscribeTotpTick(tick)
  }, [generator])

  if (state == null) {
    return null
  }
  if (state.error === true) {
    return <span className={classNames(styles2.CardInput)}>{t('record.totp.invalid')}</span>
  }

  const formatted = formatTotpCode(state.code)
  // 기기 시계가 서버와 크게 어긋난 사용자에게만 정보성 안내(코드는 timeSync가 자동 보정 — 설계 §확정결정 3).
  // driftMs는 캘리브레이션 시점 고정 측정값이라 절전/수동변경 노이즈가 유입되지 않는다.
  const showDrift = state.driftMs != null && Math.abs(state.driftMs) >= CLOCK_DRIFT_BANNER_MS

  return (
    <React.Fragment>
      <div
        className={classNames(styles2.CardInput, 'borderless')}
        style={{ cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        onClick={() => onCopyCode(state.code)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onCopyCode(state.code)
          }
        }}
      >
        {showDrift ? (
          <div style={{ fontSize: 12, color: '#E0663B', marginBottom: 4 }}>
            {t('record.totp.clockDrift', { seconds: Math.round(Math.abs(state.driftMs) / 1000) })}
          </div>
        ) : null}
        {/* 스타일은 목록 칩과 공유 — 인라인으로 각각 두었더니 letterSpacing 이 어긋나
            같은 코드가 화면마다 다른 굵기로 보였다(totpCodeStyle 주석 참조) */}
        <span style={getTotpCodeStyle()}>{formatted}</span>
        {/* CardInput 이 space-between flex 라 중앙에 떠 보임 — auto 마진으로 우측 정렬(QA 피드백) */}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center' }}>
          <TotpCountdownRing
            secondsRemaining={state.secondsRemaining}
            period={state.period}
            ariaLabel={t('record.totp.secondsRemaining', { seconds: state.secondsRemaining })}
          />
        </span>
        {isTotpViewAllowed() && featureFlags.totpExport ? (
          // 자체 테두리 칩 → SecureField 눈 버튼과 동일한 antd text 아이콘 버튼(디자인 통일, 2026-08-04)
          <Button
            type="text"
            size="small"
            aria-label={t('record.totp.qrButton')}
            icon={<QrcodeOutlined style={{ fontSize: 18, color: '#6b7280' }} />}
            style={{ marginLeft: 10 }}
            onClick={(e) => {
              e.stopPropagation() // 루트 div 클릭=코드 복사와 분리(오발동 방지)
              if (onShowQr) onShowQr() // QR 열람도 열람 감사 이벤트(동일 command) 기록
              setQrOpen(true)
            }}
            onKeyDown={(e) => {
              // 루트 div 의 Enter/Space=복사(:onKeyDown)로 버블되면 QR 열기와 복사가
              // 동시 발동 — 해당 키만 차단(Tab 등 내비게이션 키는 전파 유지, 리뷰 반영)
              if (e.key === 'Enter' || e.key === ' ') e.stopPropagation()
            }}
          />
        ) : null}
      </div>
      {/* 모달은 복사 클릭 영역(위 div) 밖에 렌더 — antd Modal 은 portal 이지만 React
          합성 이벤트는 React 트리를 따라 버블되므로, div 자식으로 두면 모달 내부의
          모든 클릭이 onCopyCode(복사+감사 이벤트)로 올라간다(PR #1537 리뷰 major) */}
      {qrOpen ? (
        <TotpQrModal
          open={qrOpen}
          uri={getTotpDisplayUri(content, recordTitle)}
          title={recordTitle}
          onClose={() => setQrOpen(false)}
        />
      ) : null}
    </React.Fragment>
  )
}

// QR 표시용 URI — 저장값이 URI 면 원본 그대로(파라미터 보존). bare secret 분기는
// 현 UI 흐름에서 도달 불가(코드 생성 실패=invalid 표시라 QR 버튼 자체가 없음 —
// 정상 저장은 항상 URI 화, PR1)이며 함수 단독 재사용 대비 방어로만 유지(리뷰 명시).
function getTotpDisplayUri(content, recordTitle) {
  try {
    const raw = (content || '').trim()
    if (raw.toLowerCase().startsWith('otpauth://')) return raw
    return normalizeToOtpauthUri(raw, { account: recordTitle || '' })
  } catch (e) {
    return null
  }
}

// TOTP 편집 입력: 저장값(otpauth URI)에서 secret만 꺼내 표시·수정하고, form에는 원본 파라미터
// (algorithm/digits/period/issuer)가 보존된 URI를 기록한다(표시↔저장 분리).
// rc-field-form은 Form.Item이 주입하는 value가 자식의 명시 value를 덮어쓰고, onChange는
// e.target.value를 store에 먼저 기록하므로(Field.getControlled) — 분리는 반드시
// getValueProps(빈 객체 반환: value 주입 차단) + getValueFromEvent(변환값 기록)로 구현해야 한다.
// export는 통합 테스트(TotpFieldEdit.test.js — 실제 antd Form과의 getValueProps/getValueFromEvent 계약 검증)용.
// qrImage 판정 결과 → 사용자 문구. UNSUPPORTED 는 폼 검증(recordValidation)과 문구를 공유한다.
const TOTP_PASTE_ERROR_KEYS = {
  NO_QR: 'record.totp.paste.error.noQr',
  MIGRATION_QR: 'record.totp.paste.error.migrationQr',
  NOT_TOTP_QR: 'record.totp.paste.error.notTotpQr',
  EMPTY_SECRET: 'record.totp.paste.error.emptySecret',
  // 시드는 있으나 period/digits/algorithm 등이 잘못된 QR — 기존 키 재사용(신규 문구 불필요).
  INVALID_QR: 'record.totp.invalid',
  UNSUPPORTED: 'record.totp.unsupported',
}

export const TotpFieldEdit = ({
  id,
  inputAttrs,
  rules,
  copyRef,
  copyElem,
  initialValue,
  delayedCheckChanged,
  messageApi,
}) => {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(true)
  const [pasting, setPasting] = useState(false)

  // Basic(JP·PAID)은 totp 값 편집 불가 — 조회(코드 미리보기·복사·눈 버튼)는 유지(§4.2).
  // ⚠️ 2026-08-26 부터 이 disabled 가 **기존 totp 값의 유일한 방어층**이다. 그전에는 레코드
  // 편집 진입 자체가 막혀 있었는데(2026-08-24 정책), 상한 초과 첨부가 함께 있는 레코드에서
  // "초과 파일을 삭제하라" 는 안내를 따를 수 없는 교착이 생겨 폐지됐다. 서버는 저장 시 전
  // 암호화 필드를 재암호화해 값 변경을 판별할 수 없으므로(신규 추가·타입 승격만 493 으로 막음)
  // 이 disabled 를 제거하면 Basic 이 기존 인증 코드를 바꿀 수 있게 된다.
  const canEditTotp = isTotpCreateAllowed()

  // 레코드 전환(key 리마운트)·패널 닫힘 후 늦게 도착한 디코딩 결과를 버리기 위한 플래그.
  // 이 컴포넌트는 key 로 리마운트되지만(`:1709`) 이미 시작된 handlePaste 의 async 클로저는
  // 취소되지 않고, copyRef 는 RecordWrapper 가 key 없이 렌더하는 RecordEdit 의 폼 ref 라
  // 레코드가 바뀌어도 같은 인스턴스다 → 가드가 없으면 A 의 QR 시드가 B 의 폼에 주입되고
  // 저장 버튼까지 켜진다(PR #1745 리뷰 major). LoginMasterPW 의 unmounted 플래그와 같은 관례.
  const unmountedRef = useRef(false)
  useEffect(
    () => () => {
      unmountedRef.current = true
    },
    [],
  )

  // 마운트 1회 파싱: 저장값이 URI면 secret만 표시(edit), 파싱 실패면 원문 표시(raw — 손상 데이터
  // 폴백, validator가 저장 시 판단), 그 외 신규(new). remount(key) 시 새로 계산된다.
  // initialValue(=field.content, 복호 평문)를 우선 사용 — Form ref 장착/store 주입 타이밍에
  // 의존하지 않도록(PR #1489 리뷰). store fallback은 initialValue 미전달 호출 호환용.
  const initRef = useRef(null)
  if (initRef.current === null) {
    const raw = initialValue !== undefined ? initialValue : copyRef.current?.getFieldValue(id)
    const s = typeof raw === 'string' ? raw : ''
    if (s.toLowerCase().startsWith('otpauth://')) {
      try {
        initRef.current = { mode: 'edit', originalUri: s, secret: extractSecret(s) }
      } catch (e) {
        initRef.current = { mode: 'raw', originalUri: null, secret: s }
      }
    } else {
      initRef.current = { mode: 'new', originalUri: null, secret: s }
    }
  }
  const { mode, originalUri } = initRef.current
  const [secretText, setSecretText] = useState(initRef.current.secret)
  const [previewSource, setPreviewSource] = useState(
    initRef.current.mode === 'edit' ? initRef.current.originalUri : initRef.current.secret,
  )

  // 표시 텍스트 → form 저장값. 편집 모드에서 bare 재정규화는 파라미터를 기본값(SHA1/6/30)으로
  // 리셋해 비표준 TOTP를 조용히 깨뜨리므로 replaceSecret으로 보존한다. 실패(무효/빈 secret,
  // otpauth URI 통째 붙여넣기)는 원문 그대로 반환 — required/validator 차단 또는 otpauth 재해석(전체 교체).
  const toFormValue = (text) => {
    if (mode === 'edit') {
      try {
        return replaceSecret(originalUri, text)
      } catch (e) {
        return text
      }
    }
    return text
  }

  const handleChange = (e) => {
    const text = e?.target?.value || ''
    setSecretText(text)
    setPreviewSource(toFormValue(text))
  }

  // 클립보드 이미지(2FA 설정 화면 QR 스크린샷) 붙여넣기 → 시드 자동 입력.
  // 이미지가 없으면 preventDefault 하지 않아 텍스트 붙여넣기는 기존 경로를 그대로 탄다.
  const handlePaste = async (e) => {
    if (!canEditTotp) return // Basic: QR 붙여넣기도 편집 계열이라 차단
    if (pasting) return // await 사이 재진입 차단(중복 디코딩·stale 갱신 방지)
    const items = Array.from(e.clipboardData?.items || [])
    const imageItem = items.find(
      (it) => it.kind === 'file' && String(it.type || '').startsWith('image/'),
    )
    const blob = imageItem?.getAsFile?.()
    if (blob == null) return

    e.preventDefault()
    setPasting(true)
    try {
      const result = await decodeTotpQrImage(blob)
      // 이 필드 인스턴스가 사라진 뒤(레코드 전환·패널 닫힘) 도착한 결과는 통째로 버린다 —
      // 폼 주입뿐 아니라 토스트도 막는다(다른 레코드를 보는 중에 뜨면 오해를 준다).
      if (unmountedRef.current) return
      if (result.error != null) {
        messageApi?.error(t(TOTP_PASTE_ERROR_KEYS[result.error] || TOTP_PASTE_ERROR_KEYS.NO_QR))
        return
      }
      // 붙여넣은 QR 이 새 정본이 된다. initRef 를 edit 으로 갱신하지 않으면 이후 secret 수동
      // 수정이 toFormValue 의 non-edit 분기를 타 QR 의 algorithm/digits/period 가 사라진다.
      const secret = extractSecret(result.uri)
      initRef.current = { mode: 'edit', originalUri: result.uri, secret }
      setSecretText(secret)
      setPreviewSource(result.uri)
      copyRef.current?.setFieldsValue({ [id]: result.uri })
      // setFieldsValue 는 onValuesChange 를 트리거하지 않는다(rc-field-form) — FieldManager 의
      // 변경 감지가 그 콜백 하나에 걸려 있어, 직접 호출하지 않으면 저장 버튼이 켜지지 않는다.
      delayedCheckChanged?.({ [id]: result.uri })
      copyRef.current?.validateFields([id]).catch(() => {}) // 이전 검증 에러 해제
    } catch (err) {
      if (unmountedRef.current) return
      messageApi?.error(t(TOTP_PASTE_ERROR_KEYS.NO_QR)) // 시드가 섞일 값은 로그에 남기지 않는다
    } finally {
      setPasting(false)
    }
  }

  // 미리보기는 필드 폭을 줄이지 않도록 입력칸 내부(suffix)에 회전 코드로 표시(TotpFieldView 패턴).
  // 입력 시점 고정 코드는 30초 경과 시 stale이 되므로 tick으로 회전시킨다.
  const generator = useMemo(() => {
    try {
      return { fn: createGenerator(normalizeToOtpauthUri(previewSource, {})) }
    } catch (e) {
      return null
    }
  }, [previewSource])
  const [preview, setPreview] = useState(null)
  useEffect(() => {
    if (generator == null) {
      setPreview(null)
      return undefined
    }
    const tick = () => setPreview(generator.fn().code)
    tick()
    const timerId = setInterval(tick, 1000)
    return () => clearInterval(timerId)
  }, [generator])

  return (
    <Form.Item
      name={id}
      rules={rules}
      getValueProps={() => ({})}
      getValueFromEvent={(e) => toFormValue(e?.target?.value || '')}
      // 붙여넣기는 숨은 어포던스라 안내 없이는 발견되지 않는다(GitHub 첨부 힌트와 동형).
      // Basic 은 편집 불가 안내로 교체 — 값 조회·복사는 그대로 가능함을 함께 알린다.
      extra={t(
        // eslint-disable-next-line no-nested-ternary
        !canEditTotp
          ? 'record.totp.proOnlyEdit'
          : pasting
            ? 'record.totp.paste.scanning'
            : 'record.totp.paste.hint',
      )}
    >
      <Input
        {...inputAttrs}
        disabled={!canEditTotp}
        onPaste={handlePaste}
        // 형제 필드(createTextField 일반 분기)와 동일한 입력칸 규격(높이 48px·패딩·테두리) —
        // 미지정 시 antd 기본 높이로 렌더돼 폼 안에서 이 필드만 작게 보인다.
        className={classNames(styles0.passwordInput)}
        type={visible ? 'text' : 'password'}
        value={secretText}
        onChange={handleChange}
        placeholder={t('record.totp.inputPlaceholder')}
        suffix={
          <React.Fragment>
            {preview != null ? (
              <span
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '0.08em',
                  fontSize: 12,
                  color: '#5B6480',
                  whiteSpace: 'nowrap',
                  marginRight: 4,
                }}
              >
                {formatTotpCode(preview)}
              </span>
            ) : null}
            {copyElem}
            <Button
              type="text"
              icon={<img src={visible ? eyeOffIcon : eyeIcon} alt="totp" />}
              onClick={() => setVisible(!visible)}
              tabIndex={-1}
            />
          </React.Fragment>
        }
      />
    </Form.Item>
  )
}

// totp 필드 중 이번 저장에서 값이 변경된 필드 id 목록. 저장 확인 팝업 트리거 판정용(PR5.5).
// - 값이 비었으면 제외(비움/삭제는 확인 대상 아님)
// - 신규 필드(초기값 없음)에 값이 있으면 변경으로 간주(생성 모드 포함)
// 형제 checkChanged는 isEqual(깊은 비교)을 쓰지만 totp form 값은 항상 문자열이라 !==로 충분(의도적 차이).
export const getChangedTotpFieldIds = (fieldIdMap, initValues, values) =>
  Object.keys(fieldIdMap || {}).filter((id) => {
    if ((fieldIdMap[id]?.type || '').toLowerCase() !== 'totp') return false
    const current = values?.[id]
    if (current == null || current === '') return false
    return current !== (initValues?.[id] ?? '')
  })

export const getRulesByType = (type, isRequired, typeInfo) => {
  const rules = []

  if (isRequired) {
    rules.push({
      required: true,
      message: i18n.t('validation.required.invalid'),
    })
  }

  const ruleInfo = {
    isEncrypted: type === 'password',
    isNumber: type === 'pincode',
    isURL: type === 'url',
    isEmail: type === 'email' || type === 'secure_email',
    minLength: null,
  }

  if (typeInfo != null) {
    const { min_length: minLength } = typeInfo
    ruleInfo.minLength = minLength
  }

  if (ruleInfo.isEncrypted === true) {
    rules.push({
      pattern: /^[a-zA-Z0-9!@#$%^&*()_+~`|₩"}{[\]:;'<>,.?\\/=\\-]*$/g,
      message: i18n.t('validation.pwtype.invalid'),
    })
  }

  if (ruleInfo.isNumber === true) {
    rules.push({
      pattern: /^[0-9]*$/,
      message: i18n.t('validation.number.invalid'),
    })
  }

  if (ruleInfo.isURL === true) {
    rules.push({
      pattern:
        /^(https?:\/\/)?[-a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ@:%._\+~#=]{1,256}\.[a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ()]{1,6}\b([-a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ()@:%_\+.~#?&//=]*)$/g,
      message: i18n.t('validation.url.invalid'),
    })
  }

  if (ruleInfo.isEmail === true) {
    rules.push({
      pattern: EMAIL_PATTERN,
      message: i18n.t('validation.email.invalid'),
    })
  }

  // totp: 잘못된 secret/otpauth 입력을 저장 전 차단(서버 검증이 없으므로 입력 단계가 유일한 방어선)
  if (type === 'totp') {
    rules.push({
      validator: (_rule, value) => {
        if (!value) return Promise.resolve()
        try {
          normalizeToOtpauthUri(value, {})
          return Promise.resolve()
        } catch (e) {
          // 미지원 방식(HOTP·Steam)은 키가 틀린 게 아니라 우리가 못 다루는 것 — 한 문구로
          // 뭉개면 사용자가 있지도 않은 오타를 찾는다(recordValidation 과 동일 판단, D6).
          const messageKey =
            e?.code === 'UNSUPPORTED_TOTP' ? 'record.totp.unsupported' : 'record.totp.invalid'
          return Promise.reject(new Error(i18n.t(messageKey)))
        }
      },
    })
  }

  if (ruleInfo.minLength != null && ruleInfo.minLength > 0) {
    const regExStr = `^.{${ruleInfo.minLength},}$`
    rules.push({
      pattern: new RegExp(regExStr, 'g'),
      message: i18n.t('validation.minLength.invalid', { length: ruleInfo.minLength }),
    })
  }

  return rules
}

const EditSecureTextField = ({
  id,
  inputAttrs,
  rules,
  copyRef,
  custom,
  copyElem,
  type,
  delayedCheckChanged,
  delayedValidate,
}) => {
  const [visible, setVisible] = useState(true)
  const [strength, setStrength] = useState(0)
  const inputRef = useRef(null)
  const [inputWidth, setInputWidth] = useState(0)
  let currentValue = copyRef?.current?.getFieldValue(id) || ''

  const handlePasswordChange = (e) => {
    const value = e?.target?.value || ''
    const strengthScore = passwordStrength({
      value,
    }).calculate()
    setStrength(strengthScore)
    currentValue = value
  }

  // 기존 비밀번호 로직 처리
  // 추가 onChange 이벤트 핸들러 실행
  const handleChange = (e) => {
    handlePasswordChange(e)
    if (inputAttrs?.onChange) {
      inputAttrs.onChange(e)
    }
    handleMaxLength(e)
  }

  useEffect(() => {
    const strengthScore = passwordStrength({
      value: currentValue,
    }).calculate()
    setStrength(strengthScore)
  }, [currentValue])

  const handleResize = () => {
    requestAnimationFrame(() => {
      const width = inputRef?.current?.input?.offsetWidth
      if (width && width !== inputWidth) {
        setInputWidth(width)
      }
    })
  }

  useEffect(() => {
    handleResize() // 초기 사이즈 측정
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <Form.Item name={id} rules={rules} className={classNames(styles0.passwordStrengthInput)}>
      <Input
        className={classNames('passwordStrength')}
        type={visible ? 'text' : 'password'}
        onChange={handleChange}
        value={copyRef.current?.getFieldValue(id) || ''}
        ref={inputRef}
        addonAfter={
          !custom && (type === 'password' || type === 'pincode') ? (
            <div>
              <Progress
                percent={passwordStrength({ strength }).labels}
                percentPosition={{ align: 'center', type: 'inner' }}
                size={[inputWidth + 100, 4]}
                showInfo={false}
                strokeColor={passwordStrength({ strength }).color}
              />
              {currentValue ? <span>{passwordStrength({ strength }).text}</span> : null}
            </div>
          ) : null
        }
        suffix={
          <React.Fragment>
            {copyElem}
            <Button
              type="text"
              icon={<img src={visible ? eyeOffIcon : eyeIcon} alt="password" />}
              onClick={() => setVisible(!visible)}
              tabIndex={-1}
            />
            {(type === 'password' || type === 'pincode') && (
              <Button
                type="text"
                icon={<img src={keyIcon} alt="key" />}
                tabIndex={-1}
                onClick={() => {
                  confirmBase({
                    wrapClassName: classNames(styles.passwordGeneratorModal),
                    title: i18n.t('record.passwordGenerator.title'),
                    content: (
                      <PasswordGenerator
                        setValue={(password) => {
                          currentValue = password
                        }}
                        type={type}
                      />
                    ),
                    open: true,
                    maskClosable: false,
                    okText: i18n.t('common.button.create1'),
                    onOk: () => {
                      copyRef.current?.setFieldsValue({ [id]: currentValue })
                      handlePasswordChange({ target: { value: currentValue } })
                      delayedCheckChanged(copyRef.current?.getFieldValue(id))
                      delayedValidate(copyRef.current?.getFieldValue(id))
                    },
                  })
                }}
              />
            )}
          </React.Fragment>
        }
        {...inputAttrs}
      />
    </Form.Item>
  )
}

const createFilesElem = (field, options, rules = [], isChecked) => {
  const { id, fileInfos } = field
  const { isEdit, rsKeyObj, attachmentManager, attachmentAttrs, addAttachment, deleteAttachment } =
    options

  const idEnalbledValidateCheck = isChecked === true

  // required 규칙을 validator로 대체
  const modifiedRules = rules.map((rule) => {
    if (rule.required === true) {
      return {
        required: true,
        validator: async () => {
          // field가 touched이거나 preCheckForm이 false일 때 validation 체크
          if (idEnalbledValidateCheck) {
            if (fileInfos?.length > 0) {
              return Promise.resolve()
            }
            throw new Error()
          }
          return Promise.resolve()
        },
      }
    }
    return rule
  })

  let validateProps = {
    validateStatus: 'success',
    help: undefined,
  }

  if (idEnalbledValidateCheck) {
    if (field.custom) {
      validateProps = {
        validateStatus: 'success',
        help: undefined,
      }
    } else {
      validateProps = {
        validateStatus: 'success',
        help: undefined,
      }
      if (rules.length > 0) {
        if (!fileInfos?.length) {
          validateProps.validateStatus = 'error'
          validateProps.help = i18n.t('validation.required.invalid')
        }
      }
    }
  }

  const attachmentButton =
    isEdit === true ? (
      <Form.Item name={id} rules={modifiedRules} {...validateProps}>
        <Button
          className={classNames(styles.AddAttachmentButton)}
          type="text"
          style={{ justifyContent: 'flex-start' }}
          onClick={() => {
            if (addAttachment != null) {
              addAttachment(field)
            }
          }}
          icon={<img src={paperClip24SVG} alt="attachment" />}
        >
          {i18n.t('appConfig.field.addAttachments')}
        </Button>
      </Form.Item>
    ) : null

  if (id == null) {
    field.id = createPropertyId()
  }

  const attachmentElems = (fileInfos || []).map((fileInfo) => {
    let attachmentObj = fileInfo.attachment
    if (attachmentObj == null) {
      attachmentObj = attachmentHelper.createAttachmentByResource(
        { ...fileInfo, resource_id: field.resource_id },
        rsKeyObj,
        {
          deleteAttachment: () => {
            if (deleteAttachment != null) {
              deleteAttachment(field, attachmentObj)
            }
          },
        },
        attachmentManager,
      )
      fileInfo.attachment = attachmentObj
    }

    const { key, uploaded } = attachmentObj
    const attrs = {
      attachmentObj,
      enablePreview: false,
      downloadable: uploaded,
      ...attachmentAttrs,
    }

    return <Attachment key={key} {...attrs} />
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {attachmentElems}
      {attachmentButton}
    </div>
  )
}

const getLabelFromField = (field, lableMap, defaultValue) => {
  // const { label, label_key: labelKey } = field;
  const { label } = field
  let labelText = null
  if (label != null && lableMap != null) {
    // @todo - label key가 있는 경우, label key를 이용해서 label을 가져온다.
    labelText = lableMap[label]
  }
  if (labelText == null) {
    labelText = label || defaultValue || ''
  }
  return labelText
}

function openEditText(opts0) {
  const opts = opts0 || {}

  const extObj = {
    text: opts.text,
    newText: opts.text,
    ref: React.createRef(),
    okDisabled: true,
  }

  extObj.formAttrs = {
    onValuesChange: (changedValues) => {
      if (changedValues.text != null) {
        extObj.newText = changedValues.text
        extObj.okDisabled = extObj.newText.trim().length === 0 || extObj.text === extObj.newText
        extObj.updateModal()
      }
    },
    initialValues: {
      text: extObj.text,
    },
  }

  extObj.render = () => {
    return (
      <Form {...extObj.formAttrs}>
        <Form.Item name="text">
          <Input />
        </Form.Item>
      </Form>
    )
  }

  const modalCfg = {
    title: opts.title,
    wrapClassName: classNames(styles0.editLabelPopup),
    icon: null,
    content: extObj.render(),
    okText: opts.okText || i18n.t('common.button.ok'),
    cancelText: i18n.t('common.button.cancel'),
    maskTransitionName: 'maskTransitionName',
    width: 400,
    // style: { height: '600px' },
    closable: false,
    maskClosable: true,
    okButtonProps: {
      disabled: extObj.okDisabled,
      className: classNames('type1', 'h38'),
      style: { minWidth: '100px' },
    },
    cancelButtonProps: {
      className: classNames('h38'),
      style: { minWidth: '100px' },
    },
    onOk: (closeFunc) => {
      if (opts.onOk != null) {
        opts.onOk(
          {
            text: extObj.newText,
          },
          closeFunc,
        )
      } else {
        closeFunc()
      }
    },
    onCancel: () => {
      if (opts.onCancel != null) {
        opts.onCancel('cancel')
      }
    },
    afterClose: () => {
      if (opts.afterClose != null) {
        opts.afterClose()
      }
    },
  }

  extObj.modalObj = confirmBase(modalCfg)

  extObj.updateModal = () => {
    extObj.modalObj.update({
      okButtonProps: {
        disabled: extObj.okDisabled,
        className: classNames('type1', 'h38'),
        style: { minWidth: '100px' },
      },
    })
  }

  return extObj
}

// 익스텐션 자동 입력 대상 타입 — 필드 라벨 배지·타입 셀렉트 아이콘 표시용.
// totp(인증 코드)도 익스텐션이 자동 입력하므로 포함한다.
const isAutofillTargetType = (type) =>
  type === 'id' || type === 'url' || type === 'password' || type === 'totp'

// autofill 연동용 dev-only 타입(운영 숨김 필터 전용) — totp 는 노출을
// totpAuthenticator 플래그(isFeatureHiddenType)가 별도로 관장하므로 제외.
const isAutofillDevOnlyType = (type) => type === 'id' || type === 'url' || type === 'password'

const renderRequiredMark = (required) => {
  if (required !== true) return null
  return <img src={requiredBadgeIcon} alt="required" />
}

const renderAutofillBadge = (type) => {
  if (!featureFlags.autofillIcon || !isAutofillTargetType(type)) return null
  const tooltipKey = type === 'url' ? 'record.field.autofillUrl' : 'record.field.autofill'
  return (
    <Tooltip title={i18n.t(tooltipKey)}>
      <img src={autofillIcon} alt="autofill" />
    </Tooltip>
  )
}

const getLabelElem = (field, isLeakedEmail = false) => {
  const { encrypt, label, newLabel, type, required } = field
  const labelText = newLabel || label
  let lockIconElem = null

  if ((encrypt === true && isLeakedEmail === false) || (encrypt == null && type === 'files')) {
    lockIconElem = <img src={lockIcon} alt="lock" />
  }

  return (
    <div className={classNames(styles0.fieldItemContainer)}>
      <div className={classNames(styles0.fieldLabel)}>
        <span className={classNames(styles0.label)}>
          {labelText}
          {isLeakedEmail && <img src={redLockIcon} alt="red lock" />}
        </span>
        {lockIconElem}
        {renderRequiredMark(required)}
        {renderAutofillBadge(type)}
      </div>
    </div>
  )
}

const getCustomLabelElem = (field, onChangeLabel, isLeakedEmail = false) => {
  const { encrypt, label, newLabel, type, required } = field
  const labelText = newLabel || label
  let lockIconElem = null

  if (encrypt === true || (encrypt == null && type === 'files')) {
    lockIconElem = <img src={lockIcon} alt="lock" />
  }

  const onClickEdit = () => {
    openEditText({
      title: i18n.t('record.field.editLabel'),
      text: labelText,
      onOk: (values, closeFunc) => {
        onChangeLabel(values.text)
        closeFunc()
      },
    })
  }

  const labelStyle = isLeakedEmail ? { gap: '4px', color: 'red' } : {}

  return (
    <div
      className={classNames(styles0.fieldLabel, 'custom')}
      style={isLeakedEmail ? { color: 'red' } : {}}
    >
      <span className={classNames(styles0.label)} style={labelStyle}>
        {labelText}
      </span>
      <Button
        className={classNames(styles0.editLabel)}
        type="text"
        onClick={onClickEdit}
        tabIndex={-1}
      >
        {i18n.t('record.field.editLabel')}
      </Button>
      {lockIconElem}
      {renderRequiredMark(required)}
      {renderAutofillBadge(type)}
    </div>
  )
}

const handleMaxLength = (e) => {
  const { value } = e.target
  const trimmed = value.slice(0, 256)
  if (value.length > 256) {
    e.target.value = trimmed // 시각적으로도 잘림 적용
  }
}

// 새로운 아이디 생성 로직
const newIdMap = {}

// id 생성
export const createPropertyId = () => {
  const curTime = Date.now()
  let newID = `id_${curTime}`
  let idx = 0

  while (newIdMap[newID] != null) {
    newID = `id_${curTime}_${idx}`
    idx += 1
  }
  newIdMap[newID] = true
  return newID
}

// ------------------- FileMananger 2 -------------------
// React-sortable 사용.
export function FieldManager(
  resource,
  rsKeyObj,
  ref,
  onStateChange,
  attachmentOptions,
  messageApi,
  decryptedRecordItem,
) {
  const self = this

  self.rsKeyObj = rsKeyObj
  self.ref = ref
  self.attachmentOptions = attachmentOptions
  self.decryptedRecordItem = decryptedRecordItem
  self.templateInfo = {
    templateList: [],
    typeList: [],
    titleTranslations: null,
    fieldLangMap: {},
  }

  self.fieldIdMap = {}
  self.initValues = {}
  self.templateRes = null

  self.prevChanged = false

  self.isChecked = null

  self.initInfo = {
    basicFields: [],
    customFields: [],
  }

  self.copyObjMap = {}

  self.onStateChange = onStateChange

  console.log(' --- decryptedRecordItem : ', decryptedRecordItem)

  self.notifyStateChanged = (value) => {
    if (self.onStateChange != null) {
      self.onStateChange(value)
    }
  }

  const checkFieldIdMap = (fieldItem) => {
    if (self.fieldIdMap[fieldItem.id] == null) {
      // self.fieldIdList.push(fieldItem.id)
      self.fieldIdMap[fieldItem.id] = fieldItem
    } else {
      throw new Error(`duplicate field id. id: ${fieldItem.id}`)
    }
  }

  const removeFieldFromIdMap = (id) => {
    if (self.fieldIdMap[id] != null) {
      delete self.fieldIdMap[id]
    }
  }

  const createFieldData = (field, initValues) => {
    self.isChecked = false
    const { id, type } = field
    const fieldItem = {
      ...field,
    }

    if (id == null) {
      fieldItem.id = createPropertyId()
    }

    checkFieldIdMap(fieldItem)

    if (type === 'files') {
      const fileInfos = (fieldItem.content || []).map((fileItem) => {
        return { ...fileItem }
      })

      fieldItem.fileInfos = fileInfos
    }

    const value = self.getValueForRead(fieldItem.id, fieldItem.content)
    if (value != null) {
      fieldItem.content = value
      initValues[fieldItem.id] = value
    }

    return fieldItem
  }

  self.init = () => {
    const reduxState = reduxStore.getState()
    const { locale } = reduxState.settings

    self.locale = locale

    const basicFields = []
    const customFields = []
    let isCreate = false
    let templateId = null

    if (resource == null || resource.items == null) {
      isCreate = true
    } else {
      isCreate = false
      templateId = resource.template.id

      resource.items.forEach((item) => {
        if (item.custom === true) {
          customFields.push(item)
        } else {
          basicFields.push(item)
        }
      })
    }

    self.isCreate = isCreate
    self.templateId = templateId

    const initValues = {
      templateId: this.templateId,
      // title값은 따로 처리됨.
      requiredTitle: resource != null ? resource.title : '',
    }

    self.attachmentManager = attachmentHelper.createAttachmentManager(self.attachmentOptions)

    try {
      // 템플릿 캐시가 있으면 재사용, 없으면 API 호출
      const getTemplateData = () => {
        if (templateCache !== null) {
          console.log('Using cached template data')
          return Promise.resolve(templateCache)
        }

        // 이미 진행 중인 요청이 있으면 재사용
        if (templateCachePromise !== null) {
          console.log('Reusing existing template request')
          return templateCachePromise
        }

        console.log('Fetching new template data')
        templateCachePromise = ResourceTemplateService.getAll()
          .then((res) => {
            if (res.code === 200) {
              templateCache = res
              templateCachePromise = null
              return res
            }
            templateCachePromise = null
            return null
          })
          .catch((error) => {
            templateCachePromise = null
            throw error
          })

        return templateCachePromise
      }

      getTemplateData().then((res) => {
        if (res && res.code === 200) {
          console.log('res', res)
          const { data: tList, data_types: dataTypes, translations } = res
          const titleTranslations = translations != null ? translations.data : null
          const fieldTranslations = translations != null ? translations.data_types : null
          // 메모 템플릿은 전용 생성 흐름(MemoEditorModal)이 따로 있으므로, 패스워드 생성
          // 패널(openItem('create'))의 템플릿 셀렉트·기본 템플릿 후보에서 제외한다.
          // 인증 코드(OTP) 전역 템플릿은 flag OFF 빌드(prod)에서 숨김 — 판별/사유는
          // authenticatorTemplate.js 참조 (admin 템플릿 목록과 공유).
          self.templateInfo = {
            templateList: (tList || []).filter(
              (item) => !isMemoTemplate(item) && !shouldHideAuthenticatorTemplate(item),
            ),
            typeList: dataTypes || [],
            titleTranslations,
            fieldLangMap: self.makeLangMap(fieldTranslations),
          }

          // template 정보에서 typeList 정보가 있어야 component를 생성할 수 있음.
          self.initFields(initValues, basicFields, customFields)

          const { templateList } = self.templateInfo
          // create resource인 경우, 이 정보로 select box 표시.
          if (templateList.length > 0) {
            if (self.templateId == null) {
              const webLoginTemplate = templateList.find((item) => {
                return item.name === 'Web Login'
              })

              if (webLoginTemplate != null) {
                self.templateId = webLoginTemplate.id
              } else {
                self.templateId = templateList[0].id
              }

              if (self.isCreate === true) {
                self.applyTemplateId(self.templateId)
              }
            }
          }

          const formInfo = self.generateForm()
          self.notifyStateChanged({ formInfo })
        }
      })
    } catch (error) {
      console.log(error)
    }
  }

  self.initFields = (initValues, basicFields, customFields) => {
    self.basicFields = basicFields.map((field) => {
      return createFieldData(field, initValues)
    })

    self.customFields = customFields.map((field) => {
      return createFieldData(field, initValues)
    })

    self.initValues = initValues

    if (self.ref.current != null) {
      self.ref.current.setFieldsValue(this.initValues)
    }

    // field의 순서를 저장.
    self.initInfo = {
      basicFields: [...self.basicFields],
      customFields: [...self.customFields],
    }

    this.makeCustomTypeList()
  }

  const getCurrentFieldsInfo = () => {
    const values = self.ref.current.getFieldsValue()

    const info = {
      basicFields: [...self.basicFields],
      customFields: [...self.customFields],
    }

    info.basicFields.forEach((field) => {
      if (values[field.id] != null) {
        const value = values[field.id] // 표시되는 데이터 그대로 전달해야 함.
        field.content = value
      }
    })

    info.customFields.forEach((field) => {
      if (values[field.id] != null) {
        const value = values[field.id] // 표시되는 데이터 그대로 전달해야 함.
        field.content = value
      }
    })

    return info
  }

  const getFieldData = (fieldItem, initValues) => {
    const { type, content } = fieldItem

    checkFieldIdMap(fieldItem)

    if (type !== 'files') {
      initValues[fieldItem.id] = content
    }
  }

  // basicFields에 있으면 basicFields에서, customFields에 있으면 customFields에서 찾아서 리턴.
  const restoreFieldsInfo = (fieldsInfo) => {
    if (fieldsInfo == null) {
      return
    }

    const { basicFields: prevBasicFields, customFields: prevCustomFields } = fieldsInfo
    const { basicFields, customFields } = self

    prevBasicFields.forEach((field) => {
      const idx = basicFields.findIndex((item) => {
        return item.label === field.label
      })
      if (idx > -1) {
        const oldValue = basicFields[idx]
        basicFields[idx] = field
        // 기본 field의 기본 속성 유지.
        field.custom = false
        field.required = oldValue.required
      } else {
        let hasValue = null
        if (field.type === 'files') {
          hasValue = field.fileInfos != null && field.fileInfos.length > 0
        } else {
          hasValue = field.content != null && field.content.length > 0
        }

        if (field.isNew !== true || hasValue === true) {
          // custom으로 이동하는 경우에는 required 속성 제거.
          field.custom = true
          field.required = false
          customFields.push(field)
        }
      }
    })

    prevCustomFields.forEach((field) => {
      field.required = false
      customFields.push(field)
    })

    self.fieldIdMap = {}

    basicFields.forEach((field) => {
      getFieldData(field, self.initValues)
    })
    customFields.forEach((field) => {
      getFieldData(field, self.initValues)
    })
  }

  // 상세 template 정보를 가져와서 적용
  self.applyTemplateId = (id) => {
    this.templateId = id

    ResourceTemplateService.get(id)
      .then((res) => {
        if (res.code === 200) {
          const { data } = res
          self.templateRes = JSON.parse(data.content)
          self.isChecked = false
          console.log(' --- template Info : ', id, JSON.parse(data.content))

          const content = JSON.parse(data.content)
          const { base_schema: baseSchema, translations } = content
          const { ui_widgets: uiWidgets, required_uids: reqUIDs } = baseSchema

          const requiredUIDs = reqUIDs || []

          const langMap = self.makeLangMap(translations)
          const labelMap = self.getLabelMap(langMap)

          self.applyUiWidgets(uiWidgets, requiredUIDs, labelMap)
        }
      })
      .catch((error) => {
        console.warn('Template not found, using default template:', error)
        self.templateId = null
        self.templateRes = null
        const formInfo = self.generateForm()
        self.notifyStateChanged({ formInfo })
      })
  }

  self.applyUiWidgets = (uiWidgets, requiredUIDs, labelMap) => {
    // const initValues = {};
    // Resource의 title과 중복되는 title field는 제외하고 field들을 생성해야 함.

    const basicFields = uiWidgets
      .filter((field) => {
        return field.uid !== '-1'
      })
      .map((field) => {
        const { uid, type } = field

        const { encrypt } = this.getTypeInfo(type)

        const required = requiredUIDs.includes(uid)

        let label = getLabelFromField(field, labelMap, type)
        if (label == null || label.trim().length === 0) {
          const typeField = self.getTypeInfo(type)
          const typeLabelMap = self.getLabelMap(self.templateInfo.fieldLangMap)
          label = getLabelFromField(typeField, typeLabelMap, type)
        }

        const fieldItem = {
          ...field,
          label,
          required: required === true,
          id: createPropertyId(),
          isNew: true,
        }

        delete fieldItem.uid
        delete fieldItem.label_key

        if (type === 'files') {
          // files에는 encrypt가 없고 content에 포함된 file item마다 encrypt 속성이 있음.
          fieldItem.fileInfos = []
        } else {
          fieldItem.encrypt = encrypt === true
        }

        // 기존의 값이 있는 경우에는 여기에서 값을 적당히 처리한다.
        // const value = self.getFieldValue(fieldItem)
        // if (value != null) {
        //   initValues[fieldItem.id] = value
        // }
        return fieldItem
      })

    const customFields = []

    self.initFields({ templateId: self.templateId }, basicFields, customFields)

    restoreFieldsInfo(self.prevFieldsInfo)

    const formInfo = self.generateForm()

    self.notifyStateChanged({ formInfo })
  }

  self.getFormatById = (id) => {
    const field = self.fieldIdMap[id]
    if (field != null) {
      const typeInfo = self.getTypeInfo(field.type)
      if (typeInfo != null) {
        return typeInfo.format
      }
    }
    return null
  }

  self.getValueForRead = (id, originData) => {
    let ret = null
    const format = self.getFormatById(id)
    if (format != null) {
      switch (format) {
        case 'date':
        case 'dateTime':
        case 'time':
          ret = originData != null ? dayjs(originData) : null
          break
        default:
          ret = originData
      }
    } else {
      ret = originData
    }
    return ret
  }

  // 저장 확인 팝업 트리거 판정(PR5.5) — initValues/fieldIdMap 소유자로서 순수 함수에 위임
  self.getChangedTotpFieldIds = (values) =>
    getChangedTotpFieldIds(self.fieldIdMap, self.initValues, values)

  self.getFieldForSave = (id, value) => {
    let ret = null
    const format = self.getFormatById(id)
    if (format != null) {
      switch (format) {
        case 'date':
          ret = value != null ? value.format('YYYY-MM-DD') : null
          break
        case 'dateTime':
          ret = value != null ? value.format('YYYY-MM-DD HH:mm:ss') : null
          break
        case 'time':
          ret = value != null ? value.format('HH:mm:ss') : null
          break
        default:
          ret = value
      }
    } else {
      ret = value
    }
    return ret
  }

  self.getCopyElem = (name, initValue, type) => {
    return (
      <CopyPopoverComponent
        name={name}
        copyRef={self.ref}
        funcMap={self.copyObjMap}
        initValue={initValue}
        type={type}
      />
    )
  }

  self.createTextField = (field, rules, typeInfo) => {
    const { id, encrypt, type, custom } = field
    console.log(' -- createTextField : ', field, typeInfo, type)

    let elem = null

    const inputAttrs = {}

    const fieldInfo = {
      maxLength: null,
      minLength: null,
      isNumber: type === 'pincode',
      encrypt: encrypt === true,
    }

    if (typeInfo != null) {
      const { min_length: minLength, max_length: maxLength } = typeInfo
      if (fieldInfo.isNumber === false) {
        fieldInfo.isNumber = typeInfo.format === 'number'
      }
      if (fieldInfo.encrypt === false) {
        fieldInfo.encrypt = typeInfo.encrypt === true
      }
      fieldInfo.maxLength = maxLength
      fieldInfo.minLength = minLength
      inputAttrs.maxLength = maxLength
    }

    if (fieldInfo.isNumber) {
      inputAttrs.onInput = (e) => {
        e.target.value = e.target.value.replace(/[^0-9]/g, '').replace(/(\..*)\./g, '$1')
      }
    }

    const copyElem = self.getCopyElem(id, field.content, type)

    if (fieldInfo.encrypt === true) {
      const fieldAttrs = {
        id,
        copyRef: self.ref,
        inputAttrs,
        rules,
        custom,
        type,
        copyElem: id != null && type !== 'note' ? copyElem : null,
        messageApi, // TotpFieldEdit 의 QR 붙여넣기 실패 안내(토스트)용
        delayedCheckChanged: (values) => self.delayedCheckChanged(values),
        delayedValidate: (values) => self.delayedValidate(self.ref.current.getFieldValue(values)),
      }
      elem =
        // view 계열 게이트 — 편집 폼에서도 기존 TOTP 필드는 렌더 유지(다운그레이드 고객의 조회 보존).
        // Basic 의 "값 편집 disabled" 는 plan_tier Phase 3 에서 TotpFieldEdit 내부에 추가 예정.
        type === 'totp' && isTotpViewAllowed() ? (
          // key: 편집 유지한 채 레코드 전환 시 내부 state(secretText)가 이전 레코드 값으로 남지 않도록
          // remount 강제. 필드 id가 레코드 간 공유될 가능성에 대비해 resource.id 결합(PR #1489 리뷰).
          <TotpFieldEdit
            key={`totp-${resource?.id || 'new'}-${id}`}
            initialValue={field.content}
            {...fieldAttrs}
          />
        ) : (
          <EditSecureTextField {...fieldAttrs} />
        )
    } else if (type === 'date') {
      elem = (
        <Form.Item name={id} rules={rules}>
          <DatePicker suffix={copyElem} suffixIcon={<img src={calendarIcon} alt="calendar" />} />
        </Form.Item>
      )
    } else {
      elem = (
        <Form.Item name={id} rules={rules}>
          <Input
            className={classNames(styles0.passwordInput)}
            suffix={<React.Fragment>{copyElem}</React.Fragment>}
            {...inputAttrs}
          />
        </Form.Item>
      )
    }

    return elem
  }

  self.createTextArea = (field, rules, typeInfo) => {
    const { id, custom } = field

    console.log(' -- createTextArea : ', field, typeInfo)
    // @todo - encrypt가 true인 경우의 처리가 필요함.

    const copyElem = id != null ? self.getCopyElem(id, field.content) : null

    return (
      <React.Fragment>
        <Form.Item name={id} rules={rules} className={styles0.textAreaItem}>
          <TextArea rows={4} margin="10px 0" className={classNames(styles.passwordInput)} />
        </Form.Item>
        {custom === false ? copyElem : null}
      </React.Fragment>
    )
  }

  const deleteAttachment = (fileField, attachmentObj) => {
    const { fileInfos } = fileField

    const pos = fileInfos.findIndex((fileInfo) => {
      return fileInfo.attachment === attachmentObj
    })
    if (pos > -1) {
      fileInfos.splice(pos, 1)
      fileField.isTouched = true
      self.isChecked = false
    }

    self.delayedCheckChanged()
    this.generate(false)
  }

  const addAttachment = (fileField) => {
    const acceptedType = '*.*'
    // 상한의 단일 출처. 플랜(tier)이 없으면 기존 10MB 를 그대로 돌려주므로 첨부 동작이 바뀌지 않는다
    // (백엔드 저장 모델도 같은 10MB 를 하드 게이트하므로 두 값은 항상 함께 움직여야 한다).
    const sizeLimit = getAttachmentSizeLimit()
    let isErrorShown = false // 🔥 중복 방지를 위한 전역 변수

    openFile(
      null,
      (url, file) => {
        console.log(' -- openFile : ', file)

        const { fileInfos } = fileField

        // 동명 파일은 **파일을 고르는 순간** 막는다. 백엔드도 거절하지만(449), 청크 업로드는
        // 수 분이 걸려 "다 올리고 나서 거절" 이 된다(사용자 QA 2026-08-14).
        // 이 콜백은 FileReader.onload 에서 비동기로, 여러 파일이면 병렬로 발화한다
        // (utils/index.js:622 Promise.all + :591 reader.onload). 그래도 각 콜백이 원자적으로
        // 실행되고 fileInfos 를 **매번 다시 읽기** 때문에, 먼저 끝난 파일이 목록에 들어간 뒤에
        // 다음 콜백이 그것을 본다 → 기존 첨부와 같은 배치의 앞선 파일을 **둘 다** 커버한다.
        // (목록 갱신을 배치로 미루는 최적화를 넣으면 이 보장이 깨진다.)
        // 안내 패턴은 아래 첨부 개수 초과 게이트와 동일하게 맞춘다(3초 중복 방지).
        if (isDuplicateFilename(fileInfos, file.name)) {
          if (!isErrorShown) {
            isErrorShown = true
            messageApi.error(i18n.t('workspace.error.449'))

            setTimeout(() => {
              isErrorShown = false
            }, 3000)
          }
          return
        }

        const attachmentObj = attachmentHelper.createAttachment(
          file,
          self.rsKeyObj,
          {
            deleteAttachment: () => {
              deleteAttachment(fileField, attachmentObj)
            },
          },
          self.attachmentManager,
        )

        const { name, type, size } = file
        const contentType = type != null && type.length > 0 ? type : 'application/unknown'
        const newFileInfo = {
          encrypt: true,
          filename: name,
          size,
          content_type: contentType,
          attachment: attachmentObj,
        }
        if (fileInfos.length < 10) {
          fileField.fileInfos = [...fileInfos, newFileInfo]
        } else if (!isErrorShown) {
          isErrorShown = true
          messageApi.error(i18n.t('workspace.error.596'))

          // 3초 후 다시 에러 메시지를 띄울 수 있도록 초기화
          setTimeout(() => {
            isErrorShown = false
          }, 3000)
        }
        fileField.isTouched = true
        self.isChecked = false
        self.delayedCheckChanged()
        this.generate(false)
      },
      acceptedType,
      sizeLimit,
      () => {
        messageApi.open({
          type: 'error',
          // 하드코딩 '10MB' 는 상한이 flag 로 바뀌면 실제 거절 기준과 어긋나므로 sizeLimit 에서 파생한다
          content: i18n.t('common.warning.filesizeLimit', {
            limit: formatSizeLimitLabel(sizeLimit),
          }),
        })
      },
      true,
    )
  }

  self.createFilesField = (field, rules) => {
    console.log(' -- createFilesField : ', field)

    return createFilesElem(
      field,
      {
        isEdit: true,
        rsKeyObj: self.rsKeyObj,
        attachmentManager: self.attachmentManager,
        attachmentAttrs: {},
        addAttachment,
        deleteAttachment,
      },
      rules,
      self.isChecked,
    )
  }

  self.createFieldElem = (field) => {
    const { type, required } = field

    const typeInfo = this.getTypeInfo(type)
    const rules = getRulesByType(type, required, typeInfo)

    if (typeInfo == null) {
      switch (type) {
        case 'files':
          return self.createFilesField(field, rules)
        case 'note':
          return self.createTextArea(field, rules)
        default:
          return self.createTextField(field, rules)
      }
    } else {
      let { format } = typeInfo
      if (Array.isArray(format) === true) {
        format = `[${format[0]}]`
      }

      switch (format) {
        case '[file]':
          return self.createFilesField(field, rules, typeInfo)
        case 'large_text':
          return self.createTextArea(field, rules, typeInfo)
        case 'text':
        default:
          return self.createTextField(field, rules, typeInfo)
      }
    }
  }

  self.createFormItem = (field, idx, isLeaked) => {
    const { custom } = field
    console.log(' -- createFormItem : ', field)

    const onChangeLabel = (text) => {
      field.newLabel = text
      self.delayedCheckChanged()
      self.generate(false)
    }

    const labelElem =
      custom === true
        ? getCustomLabelElem(field, onChangeLabel, isLeaked)
        : getLabelElem(field, isLeaked)

    let fieldElem = self.createFieldElem(field)

    // createFieldElem에서 field id가 생성될 수 있으므로 여기서 id를 가져옴.
    const { id } = field

    if (custom === true) {
      fieldElem = (
        <div className={classNames(styles0.fieldItem)}>
          <Button
            type="text"
            className={classNames(styles0.dragHandle, 'dragHandle')}
            icon={<img src={dragIcon} alt="drag" />}
            tabIndex={-1}
          />
          {fieldElem}
          <Button
            type="text"
            icon={<img src={trashIcon} alt="trash" />}
            onClick={() => self.removeField(id)}
            tabIndex={-1}
          />
        </div>
      )
    }

    // required 시각 표시는 라벨 내부에서 renderRequiredMark 로 직접 렌더 —
    // Antd 자동 `*` 와 위치가 충돌하지 않게 attrs.required 는 더 이상 설정 안 함.

    // 여기서는 Label 표시를 위해 Form.Item을 사용.
    // 실제 데이터를 읽어오는 부분에서는 name을 포함시켜야 함.
    return (
      <Form.Item key={id} label={labelElem}>
        {fieldElem}
      </Form.Item>
    )
  }

  // add button
  self.addField = (newField) => {
    if (newField.id == null) {
      newField.isNew = true
      newField.id = createPropertyId()
    }
    if (Array.isArray(self.customFields)) {
      self.customFields.push(newField)
    } else {
      self.customFields = [newField]
    }

    checkFieldIdMap(newField)

    this.makeCustomTypeList()

    this.delayedCheckChanged()

    const formInfo = self.generateForm(false)
    console.log(' --- newForm : ', formInfo)
    self.notifyStateChanged({ formInfo })
  }

  // remove button
  self.removeField = (id) => {
    const pos = self.customFields.findIndex((field) => field.id === id)

    if (pos > -1) {
      self.customFields.splice(pos, 1)
    }

    removeFieldFromIdMap(id)

    this.makeCustomTypeList()

    this.delayedCheckChanged()

    const formInfo = self.generateForm(false)
    self.notifyStateChanged({ formInfo })
  }

  self.orderChanged = (newList) => {
    if (self.customFields !== newList) {
      console.log(' -- orderChanged : ', newList)
      const updatedList = newList.map((field) => {
        const existingField = self.customFields.find((item) => item.id === field.id)
        return {
          ...field,
          content: existingField ? existingField.content : field.content,
        }
      })
      self.customFields = updatedList
      this.delayedCheckChanged()
      const formInfo = self.generateForm(false)
      self.notifyStateChanged({ formInfo })
    }
  }

  self.makeLangMap = (translations) => {
    if (translations != null) {
      const langMap = {}
      Object.keys(translations).forEach((key) => {
        const labelMap = translations[key]
        langMap[key] = labelMap
        const localeCode = key.substring(0, 2)
        if (langMap[localeCode] == null) {
          langMap[localeCode] = labelMap
        }
      })
      return langMap
    }
    return null
  }

  this.getLabelMap = (langMap) => {
    let labelMap = null
    if (langMap != null) {
      labelMap = langMap[self.locale]
      if (labelMap == null) {
        const localeCode = self.locale.substring(0, 2)
        labelMap = langMap[localeCode]
      }
    }
    return labelMap
  }

  // custom field 추가
  this.makeCustomTypeList = () => {
    const allFields = [...self.basicFields, ...self.customFields]
    const hasFiles = allFields.some((field) => field.type === 'files')

    const { typeList, fieldLangMap } = this.templateInfo
    const labelMap = self.getLabelMap(fieldLangMap)

    this.customTypeItems = typeList
      .filter((typeItem) => {
        if (typeItem.active !== true) return false
        // 기능 플래그로 숨기는 타입(예: totp 플래그 OFF) 제외 — 드롭다운/템플릿 빌더 공용 판별
        if (isFeatureHiddenType(typeItem.type_name)) return false
        // id / url / password 는 autofill 연동용 dev-only 타입 — 운영에서는 숨김.
        if (!featureFlags.autofillIcon && isAutofillDevOnlyType(typeItem.type_name)) return false
        if (hasFiles === true && typeItem.type_name === 'files') return false
        return true
      })
      .map((typeItem, idx) => {
        const { type_name: type, encrypt } = typeItem
        const fieldLabel = getLabelFromField(typeItem, labelMap, type)
        const showAutofill = featureFlags.autofillIcon && isAutofillTargetType(type)
        const showLock = encrypt === true || type === 'files'
        const field = {
          key: String(idx + 1),
          label: (
            <div className={classNames(styles0.typeItem)}>
              {fieldLabel}
              <div className={classNames(styles0.typeIconWrapper)}>
                {showAutofill ? <img src={autofillIconSecondary} alt="autofill" /> : null}
                {showLock ? <img src={lockIconSecondary} alt="lock" /> : null}
              </div>
            </div>
          ),
          onClick: () => {
            self.addFieldByType(type)
          },
        }
        return field
      })
  }

  self.getTypeInfo = (type) => {
    const { typeList } = this.templateInfo
    const typeInfo = typeList.find((typeItem) => {
      return typeItem.type_name === type
    })

    return typeInfo
  }

  function capitalizeFirstLetter(val) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1)
  }

  // type에 따른 field 추가
  self.addFieldByType = (type) => {
    const typeField = this.getTypeInfo(type)
    const { encrypt } = typeField
    const lowerType = type.toLowerCase()

    const typeLabelMap = self.getLabelMap(self.templateInfo.fieldLangMap)
    let label = getLabelFromField(typeField, typeLabelMap, type)
    if (label == null || label.trim().length === 0) {
      label = capitalizeFirstLetter(type)
    }

    const newField = {
      custom: true,
      content: null,
      encrypt,
      label,
      type: lowerType,
      id: createPropertyId(),
      isNew: true,
    }

    if (type === 'files') {
      newField.fileInfos = []
    }

    // customFieldsData 새로운 필드를 맨 앞에 추가
    // self.customFields.unshift(newField)

    // customFieldsData 새로운 필드를 맨 뒤에 추가
    self.customFields.push(newField)
    // customFieldsData 새로운 필드를 맨 앞에 추가
    checkFieldIdMap(newField)

    self.makeCustomTypeList()

    self.delayedCheckChanged()

    // 새로운 form 생성 및 업데이트
    const formInfo = self.generateForm(false)
    self.notifyStateChanged({ formInfo })

    setTimeout(() => {
      if (self.ref.current != null) {
        self.ref.current.scrollToField(newField.id)
        self.ref.current.getFieldInstance(newField.id).focus()
      }
    }, 300)
  }

  self.isLeaked = (field) => {
    const { type, content } = field
    let isLeaked = false
    console.log(' -- resource : ', resource)
    if (resource?.leakedData != null) {
      const showZeroDarkWeb = store.get('app.settings.showZeroDarkWeb', false)
      console.log(' -- showZeroDarkWeb : ', showZeroDarkWeb)

      const leakedPasswordList = resource?.leakedData?.flatMap((item) => item.data)
      const leakedEmailList = resource?.leakedData?.map((item) => item.email)

      const isLeakedEmail = leakedEmailList?.includes(content)
      const isSameLeakedPassword = regexLeakedPassword(leakedPasswordList, content)
      const isLeakedPasswordType = type === 'password' || type === 'pin'
      if (
        showZeroDarkWeb === true &&
        resource?.isLeaked === true &&
        ((isSameLeakedPassword && isLeakedPasswordType) || isLeakedEmail)
      ) {
        isLeaked = true
      }
    }
    return isLeaked
  }

  self.onTemplateChange = (value) => {
    self.prevChanged = false
    if (self.prevFieldsInfo == null) {
      self.prevFieldsInfo = getCurrentFieldsInfo()
    }
    console.log(' -- prevFieldsInfo : ', self.prevFieldsInfo)
    self.applyTemplateId(value)
  }

  self.generate = (needInitValueChange) => {
    const formInfo = self.generateForm(needInitValueChange)
    self.notifyStateChanged({ formInfo })
  }

  self.generateForm = (needInitValueChange) => {
    console.log(' -- generateForm : ', needInitValueChange)

    const basicItems = self.basicFields.map((field, idx) => {
      const isLeaked = self.isLeaked(field)

      return self.createFormItem(field, idx, isLeaked)
    })

    const customItems = self.customFields.map((field, idx) => {
      // const field = self.fieldIdMap[id]
      const isLeaked = self.isLeaked(field)

      return self.createFormItem(field, idx, isLeaked)
    })

    const dropdownAttrs = {
      menu: {
        items: this.customTypeItems,
      },
      trigger: ['click'],
      overlayClassName: classNames('customFieldDropdown'),
    }

    const addCustomItemDropdownElem = (
      <Dropdown {...dropdownAttrs}>
        <div className={classNames(styles0.addFieldContainer)}>
          <Link className={classNames(styles0.addFieldButton)}>
            <img src={plusIcon} alt="plus" />
            {i18n.t('appConfig.field.addCustomField')}
          </Link>
        </div>
      </Dropdown>
    )

    const sortableElem = (
      <React.Fragment>
        <ReactSortable
          handle=".dragHandle"
          list={self.customFields}
          setList={self.orderChanged}
          className={classNames(styles0.SortableContainer)}
        >
          {customItems}
        </ReactSortable>
      </React.Fragment>
    )

    let templateSelect = null

    const { templateList } = self.templateInfo

    if (templateList.length > 0) {
      const options = templateList.map((item) => {
        const { name, id, icon } = item
        let title = name

        // translation 처리.
        if (self.templateInfo.titleTranslations != null) {
          const templateTransations = self.templateInfo.titleTranslations[id]
          if (templateTransations != null) {
            const titleLangMap = self.makeLangMap(templateTransations)
            const titleLabelMap = self.getLabelMap(titleLangMap)
            if (titleLabelMap != null) {
              title = titleLabelMap[name] || name
            }
          }
        }

        // 보안(인증 코드) 템플릿 아이콘 'shield' → 'shield-check' 매핑은 FeatherIcon 이 일괄 처리.
        let iconElem = null
        if (icon != null) {
          const iconAttrs = {
            name: icon,
            width: '24',
            height: '24',
            stroke: '#3362FF',
          }
          iconElem = <FeatherIcon {...iconAttrs} />
        }

        const option = {
          value: item.id,
          label: (
            <div className={classNames(styles0.templateItem)}>
              {iconElem}
              <span>{title}</span>
            </div>
          ),
        }
        return option
      })

      const selectAttrs = {
        onChange: (value) => {
          this.onTemplateChange(value)
        },
        popupClassName: classNames(styles0.templateSelectPopup),
        className: classNames(styles0.templateSelect),
        options,
      }

      const formItemAttrs = {
        label: i18n.t('template.title.name'),
        name: 'templateId',
        // className: classNames({hidden: self.isCreate !== true }),
      }

      templateSelect = (
        <Form.Item {...formItemAttrs}>
          <Select {...selectAttrs} />
        </Form.Item>
      )
    }

    const copyElem = self.getCopyElem('requiredTitle', self.initValues.requiredTitle)

    const requiredTitleField = (
      <Form.Item
        label={i18n.t('record.field.title')}
        name="requiredTitle"
        normalize={(value) => value?.slice(0, 256)}
        rules={[
          {
            required: true,
            message: `${i18n.t('login.desc.requiredTitle')}`,
          },
        ]}
      >
        <Input
          className={classNames(styles0.passwordInput)}
          suffix={copyElem}
          autoFocus
          onChange={handleMaxLength}
        />
      </Form.Item>
    )

    // publishing
    const formItems = (
      <div className={classNames(styles0.fieldItemContainer)}>
        <div className={classNames(styles0.fieldContainer, 'basic')}>
          {templateSelect}
          {requiredTitleField}
          {basicItems}
          {/* {self.filesElem} */}
        </div>
        <div className={classNames(styles0.fieldContainer, 'custom')}>
          {addCustomItemDropdownElem}
          {sortableElem}
        </div>
      </div>
    )

    return {
      // false면 전달이 안됨
      initialValues: needInitValueChange !== false ? self.initValues : null,
      formItems,
      formAttrs: {
        onValuesChange: (values) => {
          // console.log(' - onValuesChange : ', values)
          self.delayedCheckChanged(values)
          self.delayedValidate(values)

          if (self.ref.current != null) {
            Object.keys(values).forEach((key) => {
              const value = values[key]
              const copyElemFunc = self.copyObjMap[key]
              if (copyElemFunc != null) {
                const prevBtnCls = copyElemFunc.getBtnCls()
                if (value != null && value.length > 0 && prevBtnCls !== '') {
                  copyElemFunc.setBtnCls('')
                } else if ((value == null || value.length === 0) && prevBtnCls !== 'empty') {
                  copyElemFunc.setBtnCls('empty')
                }
              }
            })
            self.ref.current.setFieldsValue(values)
          }
        },
      },
    }
  }

  // file type은 제거됨.
  // const createFileField = (fieldData, idx, sendFiles) => {
  //   const {
  //     type, label, custom, encrypt,
  //     filename, size, content_type: contentType, etag, id, isNew,
  //     attachment,
  //   } = fieldData

  //   let field = {
  //     type,
  //     label,
  //     custom: custom === true,
  //     encrypt,
  //     filename,
  //     size,
  //     content_type: contentType,
  //     etag,
  //     order: idx + 1,
  //   }

  //   if (isNew !== true && isCreate !== true) {
  //     field.id = id
  //   }

  //   if (attachment != null) {
  //     const { uploaded, file, resourceData, encodedFile } = attachment

  //     if (uploaded === true) {
  //       field = {
  //         ...field,
  //         ...resourceData,
  //       }
  //       if (field.encrypt === true) {
  //         sendFiles.push(encodedFile)
  //       } else {
  //         sendFiles.push(file)
  //       }
  //     }
  //   }

  //   return field;
  // }

  const applyCustomFieldLabel = (field, fieldData) => {
    const { label, custom } = fieldData
    if (custom === true) {
      field.label = fieldData.newLabel || label
    }
  }

  // chunkedFiles: 신 경로(청크 암호화 + S3 멀티파트) 대상 attachment 목록. sendFiles 와 분리하는
  // 이유 — sendFiles 는 uploadFilesToS3 가 그대로 xhr.send(file) 하는 배열이라, 아직 암호화되지
  // 않은 원본 파일을 여기에 섞으면 **평문이 S3 로 올라간다**. 채널을 물리적으로 갈라 그 사고를 막는다.
  const createFilesFieldData = async (fieldData, getOrder, sendFiles, chunkedFiles) => {
    const {
      type,
      label,
      custom,
      required,
      fileInfos,
      // id, isNew,
    } = fieldData

    const field = {
      type: type.toLowerCase(),
      label,
      custom: custom === true,
      required: required === true,
      order: getOrder() + 1,
    }

    applyCustomFieldLabel(field, fieldData)

    // files는 특수한 형태의 데이터이므로, id를 가질 수 없다.
    // @toto - 추후 id 지원이 가능해지면 여기에 id를 추가할 것.
    // if (isNew !== true && isCreate !== true) {
    //   field.id = id
    // }

    let files = null
    if (fileInfos != null) {
      files = fileInfos.map((fileInfo) => {
        const { encrypt, filename, size, content_type: contentType, attachment } = fileInfo

        const { uploaded, file, resourceData, etag, encodedFile } = attachment
        // console.log(' -- encodedFile : ', encodedFile);

        let fileField = {
          type: 'file',
          encrypt,
          filename,
          size,
          content_type: contentType,
          etag,
          order: getOrder() + 1,
        }

        if (uploaded === true) {
          fileField = {
            ...fileField,
            ...resourceData,
          }

          // sendData에서 attachment 정보를 제거
          delete fileField.attachment
        } else if (attachment.deferredChunkedUpload === true) {
          // 아직 암호화·업로드 전이다. object_key/etag/size 는 제출 시점 업로드 후
          // mapUploadResultsToSendData 가 filename 매칭으로 채운다.
          chunkedFiles.push(attachment)
        } else if (encrypt === true) {
          sendFiles.push(encodedFile)
        } else {
          sendFiles.push(file)
        }

        return fileField
      })
    }

    if (files != null) {
      field.content = files
    }

    return field
  }

  const createNormalFieldData = async (fieldData, getOrder, values) => {
    const { type, label, content, custom, required, encrypt, id, isNew } = fieldData
    const field = {
      type: type.toLowerCase(),
      label,
      content,
      custom: custom === true,
      required: required === true,
      encrypt,
      order: getOrder() + 1,
    }

    applyCustomFieldLabel(field, fieldData)

    if (isNew !== true && self.isCreate !== true) {
      field.id = id
    }

    let value = values[fieldData.id]
    const valueForSave = self.getFieldForSave(fieldData.id, value)
    if (valueForSave != null && valueForSave.length > 0) {
      if (field.encrypt === true) {
        // totp는 저장 전 정규 otpauth:// URI로 통일. 입력 유효성은 폼 검증(getRulesByType)에서 이미 확인됨.
        const plain =
          field.type === 'totp'
            ? normalizeToOtpauthUri(valueForSave, { issuer: label, account: '' })
            : valueForSave
        value = await self.rsKeyObj.encrypt(plain)
      } else {
        value = valueForSave
      }
    } else {
      value = null
    }

    field.content = value
    return field
  }

  self.validateErrorField = (fieldMap, useUI) => {
    if (self.ref.current != null) {
      // console.log(' - onValuesChange : ', this.formRef.current)
      try {
        if (fieldMap != null) {
          const fields = Object.keys(fieldMap).filter((key) => {
            const errors = self.ref.current.getFieldError(key)
            return errors.length > 0
          })
          self.ref.current.validateFields(fields, { validateOnly: useUI === false })
        } else {
          self.ref.current.validateFields({ validateOnly: useUI === false })
        }
      } catch (error) {
        console.log(' -- validate error : ', error)
      }
    }
  }

  self.fieldCheckMap = {}

  self.delayedValidate = (fieldMap) => {
    self.fieldCheckMap = { ...self.fieldCheckMap, ...fieldMap }
    if (self.validateTimer != null) {
      clearTimeout(self.validateTimer)
    }
    self.validateTimer = setTimeout(() => {
      self.validateTimer = null
      self.validateErrorField(self.fieldCheckMap)
      self.fieldCheckMap = {}
    }, 200)
  }

  const isEmpty = (value) => {
    return value == null || value.length === 0
  }

  const isEqual = (a, b) => {
    if (isEmpty(a) === true && isEmpty(b) === true) {
      return true
    }
    return a === b
  }

  self.checkChanged = () => {
    if (self.ref.current == null) {
      return false
    }

    const { basicFields: initBasicFields, customFields: initCustomFields } = self.initInfo

    const { initValues } = self
    const values = self.ref.current.getFieldsValue()

    let isChanged = false
    if (
      isEqual(initValues.requiredTitle, values.requiredTitle) === false ||
      initValues.templateId !== values.templateId
    ) {
      return true
    }

    if (
      initBasicFields.length !== self.basicFields.length ||
      initCustomFields.length !== self.customFields.length
    ) {
      return true
    }

    isChanged = initBasicFields.some((field, idx) => {
      const targetField = self.basicFields[idx]
      return field.id !== targetField.id
    })
    if (isChanged !== true) {
      isChanged = initCustomFields.some((field, idx) => {
        const targetField = self.customFields[idx]
        return field.id !== targetField.id
      })
    }

    if (isChanged !== true && initValues != null) {
      isChanged = Object.keys(self.fieldIdMap).some((key) => {
        const fieldItem = self.fieldIdMap[key]
        const { type, custom, label, newLabel } = fieldItem
        if (custom === true && newLabel != null && newLabel !== label) {
          return true
        }
        if (type === 'files') {
          const { content, fileInfos } = fieldItem
          const prevFiles = content || []
          if (prevFiles.length === fileInfos.length) {
            return prevFiles.some((prevFile) => {
              const exist = fileInfos.find((item) => item.id === prevFile.id)
              return exist == null
            })
          }
          return true
        }

        return isEqual(initValues[key], values[key]) !== true
      })
    }
    return isChanged
  }

  // activity 기록용 — 저장 시점에 어떤 필드 타입이 바뀌었는지 (편집 화면 전용)
  self.getChangedFields = () => {
    const values = self.ref?.current?.getFieldsValue() || {}
    return computeChangedFields({
      initBasicFields: self.initInfo?.basicFields,
      initCustomFields: self.initInfo?.customFields,
      basicFields: self.basicFields,
      customFields: self.customFields,
      initValues: self.initValues,
      values,
    })
  }

  self.changedMap = {}

  self.delayedCheckChanged = (changedMap) => {
    self.changedMap = { ...self.changedMap, ...changedMap }

    // console.log(' --- self.changedMap, changedMap : ', self.changedMap, changedMap)

    if (self.changeCheckTimer != null) {
      clearTimeout(self.changeCheckTimer)
    }
    self.changeCheckTimer = setTimeout(() => {
      self.checkChangedTimer = null
      const changed = self.checkChanged()

      if (changed === true && self.changedMap.templateId == null) {
        self.prevFieldsInfo = null
      }

      self.changedMap = {}

      if (self.prevChanged !== changed) {
        self.notifyStateChanged({
          changed,
        })
        self.prevChanged = changed
      }
    }, 200)
  }

  self.preCheckForm = () => {
    self.isChecked = true

    const array = self.basicFields.filter(
      (value) => value.type === 'files' && value.required === true,
    )

    const filesArray = array.map((value) => value.fileInfos)

    const hasEmptyArray = filesArray.some((arr) => arr.length === 0)

    if (hasEmptyArray) {
      self.generate(false)
    }

    return !hasEmptyArray
  }

  self.getAllFields = () => {
    return [...self.basicFields, ...self.customFields]
  }

  self.getCurrentFormValues = () => {
    return self.ref?.current?.getFieldsValue() || {}
  }

  self.getAllFieldsWithValues = () => {
    const allFields = [...self.basicFields, ...self.customFields]
    const currentValues = self.ref?.current?.getFieldsValue() || {}

    return allFields.map((field) => ({
      ...field,
      currentValue: currentValues[field.id] || field.content,
    }))
  }

  self.getSendData = async (updateKeyProps) => {
    const values = self.ref?.current?.getFieldsValue() || {}
    const files = []
    const chunkedFiles = []

    const allFields = [...self.basicFields, ...self.customFields]

    console.log(' --- allFields : ', allFields)

    let cnt = -1
    const getOrder = () => {
      cnt += 1
      return cnt
    }

    const allItems = await Promise.all(
      allFields.map(async (fieldData) => {
        const { type } = fieldData

        let field = null
        switch (type) {
          // file type은 제거됨.
          // case 'file':
          //   field = createFileField(fieldData, idx, values)
          //   break;
          case 'files':
            // 4번째 인자는 기존에 values 였으나 함수가 파라미터로 선언하지 않아 사용되지 않았다.
            field = await createFilesFieldData(fieldData, getOrder, files, chunkedFiles)
            break
          default:
            field = await createNormalFieldData(fieldData, getOrder, values)
        }

        return field
      }),
    )

    const sendData = {
      template: {
        id: self.templateId,
      },
      key: {
        enc_user_rs_key: rsKeyObj.getEncRsKey(),
        ...updateKeyProps,
      },
      title: values.requiredTitle,
      items: allItems,
    }

    console.log(' --- sendData', sendData)
    console.log(' --- files', files)

    return { sendData, files, chunkedFiles }
  }

  self.init()
}

export function ViewManager(
  rsKeyObj,
  fieldsData,
  setView,
  showCopyResult,
  recordTitle,
  recordId,
  messageApi,
  individual = null,
) {
  const self = this

  self.rsKeyObj = rsKeyObj
  self.individual = individual

  self.init = () => {
    self.fieldsData = fieldsData || []

    const newForm = self.generateView()

    setView(newForm)
  }

  self.getFieldValue = (field) => {
    switch (field.type) {
      case 'email':
      case 'string':
      case 'note':
      case 'url':
      default:
        return field.content
    }
  }

  self.createFilesView = (field, headerAccessLevel) => {
    // view를 위한 파일 관리 정보 추가.
    field.fileInfos = (field.content || []).map((fileItem) => {
      return { ...fileItem }
    })

    field.resource_id = self.individual?.id

    return createFilesElem(field, {
      isEdit: false,
      rsKeyObj: self.rsKeyObj,
      attachmentAttrs: {
        enableDelete: false, // view에서는 삭제 불가.
        headerAccessLevel,
      },
    })
  }

  self.createTextItem = (field, isLeaked = false) => {
    const { id, type, label, content, custom, encrypt } = field
    console.log(' -- createTextItem : ', id, type, label, content, custom)

    const isPasswordType = encrypt === true

    const handleNavigation = () => {
      const url =
        content.startsWith('http://') || content.startsWith('https://')
          ? content
          : `https://${content}`

      window.open(url, '_blank')
    }

    const textElem = (
      <React.Fragment>
        {content}
        <div className={classNames(styles.DetailPasswordDiv)}>
          {type === 'url' && (
            <Button
              className={classNames('disable-copy')}
              type="text"
              icon={<img src={linkIcon} alt="link" />}
              onClick={handleNavigation}
            />
          )}
        </div>
      </React.Fragment>
    )

    const actionRequest = (command) => {
      return {
        command,
        resource_id: recordId,
        resource_title: recordTitle,
        // 라벨은 사용자 지정 텍스트라 민감할 수 있다 — 타입만 보낸다 (B-2). flag OFF 면 키 생략.
        ...(featureFlags.recordChangeHistory && { field: getActivityFieldType(field) }),
      }
    }

    const copyAction = async () => {
      try {
        const request = actionRequest('user_action_copy_secure_field_data')
        const response = await ActivityService.post(request)
        console.log(' --- response : ', response)
      } catch (error) {
        console.error(error)
      }
    }

    const handleCopy = getCopyFunc({ value: content })
    const containerAttrs = {
      className: classNames(styles.CardInput, 'borderless'),
      style: isLeaked ? { color: 'red' } : {},
      onClick: async (e) => {
        const { target } = e
        if (
          target.classList.contains('disable-copy') === false &&
          target.closest('.disable-copy') == null
        ) {
          handleCopy()
          showCopyResult()
          console.log(' --- handleCopy')
          if (field.encrypt === true) {
            copyAction()
          }
        }
      },
    }

    const fieldAttrs = {
      type,
      content,
      styles2: styles,
      actionRequest: actionRequest('user_action_show_secure_field_data'),
      messageApi,
    }

    return (
      <div {...containerAttrs}>{isPasswordType ? <SecureField {...fieldAttrs} /> : textElem}</div>
    )
  }

  self.createTotpItem = (field) => {
    const postActivity = (command) =>
      ActivityService.post({
        command,
        resource_id: recordId,
        resource_title: recordTitle,
        // 라벨은 사용자 지정 텍스트라 민감할 수 있다 — 타입만 보낸다 (B-2). flag OFF 면 키 생략.
        ...(featureFlags.recordChangeHistory && { field: getActivityFieldType(field) }),
      }).catch(() => {})
    const onCopyCode = (code) => {
      getCopyFunc({ value: code })()
      showCopyResult()
      postActivity('user_action_copy_secure_field_data')
    }
    // 표시=열람으로 보고 다른 암호 필드와 동일한 감사 이벤트명 기록(트리거 시점은 mount — TotpFieldView 참조)
    const onShow = () => postActivity('user_action_show_secure_field_data')
    return (
      <TotpFieldView
        content={field.content}
        styles2={styles}
        onCopyCode={onCopyCode}
        onShow={onShow}
        onShowQr={onShow}
        recordTitle={recordTitle}
      />
    )
  }

  // 메모 본문(rich_note) 뷰 — encrypt=true 지만 SecureField 마스킹 대신 HTML 로 렌더한다.
  // BlockNote 산출 HTML — dangerouslySetInnerHTML 사용처는 반드시 sanitizeHtml 경유 (utils/sanitize)
  // 클릭 시 복사(RecordDetail 텍스트 필드와 동일) — 단 본문은 HTML 이라 보이는 텍스트로 변환해 복사.
  self.createRichNoteItem = (field) => {
    const { content } = field
    const safeHtml = sanitizeHtml(content)

    const postCopyActivity = async () => {
      try {
        await ActivityService.post({
          command: 'user_action_copy_secure_field_data',
          resource_id: recordId,
          resource_title: recordTitle,
          // 라벨은 사용자 지정 텍스트라 민감할 수 있다 — 타입만 보낸다 (B-2). flag OFF 면 키 생략.
          ...(featureFlags.recordChangeHistory && { field: getActivityFieldType(field) }),
        })
      } catch (error) {
        console.error(error)
      }
    }

    const handleCopy = () => {
      // HTML 원문 대신 보이는 텍스트(줄바꿈 보존)를 복사 — getTextFromHTML=innerText 기반
      getCopyFunc({ value: getTextFromHTML(safeHtml) })()
      showCopyResult()
      postCopyActivity()
    }

    // onClick 을 객체에 담아 spread — createTextItem 과 동일 패턴(비-대화형 div a11y 룰 회피)
    const containerAttrs = {
      // CardInput 클래스 금지 — `.CardInput p { width: 20px }` + word-break: break-all 이
      // BlockNote 문단(p.bn-inline-content)에 그대로 걸려 본문이 2글자씩 줄바꿈된다.
      // 필요한 값(text-sm·min-height·cursor)만 inline 으로 유지
      // (CardView scss 는 레거시 lint 부채로 CI 전체검사에 걸려 inline — createViewItem pre-wrap 과 동일 패턴)
      className: 'borderless',
      style: {
        // 부모 inputContainer 의 pre-wrap 이 태그 사이 공백을 빈 줄로 살리지 않도록 되돌린다
        whiteSpace: 'normal',
        // break-all 대신 — 일반 텍스트는 단어 단위로, 긴 URL 같은 통짜 문자열만 강제 줄바꿈
        overflowWrap: 'anywhere',
        fontSize: 14,
        lineHeight: '20px',
        minHeight: 32,
        cursor: 'pointer',
      },
      onClick: (e) => {
        // 본문 내 링크 클릭은 이동으로 두고 복사하지 않는다 (createTextItem 의 disable-copy 가드와 동일 취지)
        if (e.target.closest('a') != null) return
        handleCopy()
      },
      dangerouslySetInnerHTML: { __html: safeHtml },
    }

    return <div {...containerAttrs} />
  }

  self.isLeaked = (field) => {
    const { content: fieldContent } = field
    const { showZeroDarkWeb: zeroDarkWebSettingsState } = reduxStore.getState().settings
    const isZeroDarkWebActive = zeroDarkWebSettingsState == null ? false : zeroDarkWebSettingsState
    if (isZeroDarkWebActive === false) {
      return false
    }

    // console.log(' --- self.isLeaked individual', self.individual)
    // const isLeakedPasswordType = type === 'password' || type === 'pin'

    let leakedValues = []
    if (self.individual && self.individual.leaked_content) {
      const { email = [], password = [] } = self.individual.leaked_content
      leakedValues = [email, password]
      return leakedValues.includes(fieldContent)
    }
    return false
  }

  self.createViewItem = (field, headerAccessLevel) => {
    const { type, content, id } = field

    if (content != null || (Array.isArray(content) === true && content.length > 0)) {
      const isLeaked = self.isLeaked(field)
      let elem = null

      switch (type) {
        case 'files':
          elem = self.createFilesView(field, headerAccessLevel)
          break
        case 'totp':
          elem = isTotpViewAllowed()
            ? self.createTotpItem(field)
            : self.createTextItem(field, isLeaked)
          break
        case 'rich_note':
          elem = self.createRichNoteItem(field)
          break
        default:
          elem = self.createTextItem(field, isLeaked)
      }

      // isLeakedEmail 계산

      const labelElem = getLabelElem(field, isLeaked)

      return (
        <div
          className={classNames(styles.inputContainer)}
          key={id}
          style={{ whiteSpace: 'pre-wrap' }}
        >
          {labelElem}
          {elem}
        </div>
      )
    }

    return null
  }

  self.generateView = () => {
    const viewItems = self.fieldsData
      .filter((item) => item.content != null && item.content.length > 0)
      .map((field) => {
        // const field = self.fieldIdMap[id]
        return self.createViewItem(
          field,
          field.type === 'files' && self.individual?.headerAccessLevel,
        )
      })

    return <React.Fragment>{viewItems}</React.Fragment>
  }

  self.generate = () => {
    const newForm = self.generateView()
    setView(newForm)
  }

  self.init()
}

const helper = {
  FieldManager,
  ViewManager,
}

export default helper
